/**
 * Fine, always-on material halftone based on the Unicorn Mouseout reference.
 * It is evaluated independently for every authored aurora layer so the stacked
 * translucency, rather than a coarse overlay, produces the soft dotted ribbon.
 */
export const AURORA_HALFTONE_UNIFORMS = `
uniform float uHalftoneGranularity;
uniform float uHalftoneApparentZoom;
uniform vec2 uHalftoneRadius;
uniform vec2 uHalftoneFoldFadeAnisotropy;
uniform float uHalftoneLineFloor;
uniform float uHalftoneDotGain;
uniform float uHalftoneEdgeSoftness;
uniform float uHalftoneBloomGain;
uniform float uHalftoneSeed;
`;
export const AURORA_HALFTONE_HELPERS = `
float hash21(vec2 value) {
  vec3 p3 = fract(vec3(value.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float auroraHalftoneGranularity() {
  // Keep the authored pattern's screen-space scale independent from camera
  // zoom. States that do not override apparent zoom resolve it to uZoom, so
  // their existing material grid remains mathematically unchanged.
  return max(
    uHalftoneGranularity * uZoom / max(uHalftoneApparentZoom, 0.001),
    1.0
  );
}

float auroraHalftoneCellHash(
  vec2 materialPos,
  float iterationProgress,
  float granularity
) {
  vec2 cellId = floor(materialPos * granularity);
  return hash21(
    cellId + vec2(
      iterationProgress * 12.34 + uHalftoneSeed,
      uHalftoneSeed * 0.37
    )
  );
}

float auroraHalftoneDot(
  vec2 materialPos,
  vec2 gridDerivativeX,
  vec2 gridDerivativeY,
  float granularity,
  float iterationProgress,
  float radiusScale,
  float softnessScale,
  out float cellHash,
  out vec2 cellCenter
) {
  vec2 grid = materialPos * granularity;
  vec2 cellId = floor(grid);
  vec2 cellUv = fract(grid) - 0.5;
  cellHash = hash21(
    cellId + vec2(
      iterationProgress * 12.34 + uHalftoneSeed,
      uHalftoneSeed * 0.37
    )
  );
  cellCenter = (cellId + 0.5) / granularity;

  // The material grid supplies cell identity and motion, but its nonlinear
  // turbulence can shear a material-space circle into a long dash. Invert the
  // local grid Jacobian so the cell offset is measured in framebuffer pixels.
  // This keeps the center attached to the twist while restoring a round SDF.
  float determinant =
    gridDerivativeX.x * gridDerivativeY.y -
    gridDerivativeY.x * gridDerivativeX.y;
  float determinantSign = determinant < 0.0 ? -1.0 : 1.0;
  float safeDeterminant = determinantSign * max(abs(determinant), 0.000001);
  mat2 inverseGridJacobian = mat2(
    gridDerivativeY.y,
    -gridDerivativeX.y,
    -gridDerivativeY.x,
    gridDerivativeX.x
  ) / safeDeterminant;
  vec2 pixelOffset = inverseGridJacobian * cellUv;

  // Radius and softness use the same apparent camera scale as the grid. This
  // lets a state author the complete halftone pattern without altering ribbon
  // geometry, turbulence, twist, or pointer-local coordinates. Reference-lock
  // scenes use their virtual cross-axis unit instead of raw viewport height so
  // dots shrink with the stabilized ribbon on tall canvases.
  float undeformedCellPixels =
    uResolution.y / max(uCrossAxisScale, 0.001) * uHalftoneApparentZoom /
    max(uHalftoneGranularity, 1.0);
  float radiusPixels =
    mix(uHalftoneRadius.x, uHalftoneRadius.y, cellHash) *
    radiusScale * undeformedCellPixels;
  float softnessPixels = max(
    uHalftoneEdgeSoftness * softnessScale * undeformedCellPixels,
    0.5
  );
  float edgeDistancePixels = length(pixelOffset) - radiusPixels;

  // At a true fold the material mapping becomes non-invertible. Fade the dot
  // before that singularity instead of allowing the inverse to create a flash
  // or streak; the continuous line floor remains visible underneath.
  float jacobianTrace =
    dot(gridDerivativeX, gridDerivativeX) +
    dot(gridDerivativeY, gridDerivativeY);
  float discriminant = sqrt(max(
    jacobianTrace * jacobianTrace - 4.0 * determinant * determinant,
    0.0
  ));
#if AURORA_OPTIMIZED
  // The singular values multiply to |det|, so largest/smallest is
  // largest^2/|det| = (trace + disc) / (2|det|): two square roots and a
  // division fewer per layer, identical outside the fold guard's tail.
  float anisotropy = 0.5 * (jacobianTrace + discriminant) / max(abs(determinant), 0.000001);
#else
  float largestScale = sqrt(max(0.5 * (jacobianTrace + discriminant), 0.0));
  float smallestScale = sqrt(max(0.5 * (jacobianTrace - discriminant), 0.00000001));
  float anisotropy = largestScale / max(smallestScale, 0.0001);
#endif
  float foldVisibility = 1.0 - smoothstep(
    uHalftoneFoldFadeAnisotropy.x,
    uHalftoneFoldFadeAnisotropy.y,
    anisotropy
  );

  return foldVisibility * (1.0 - smoothstep(
    0.0,
    softnessPixels,
    edgeDistancePixels
  ));
}
`;
//# sourceMappingURL=halftone-shader.js.map